import { CircularProgress, makeStyles } from '@material-ui/core';

import { RiDeleteBinLine, RiFileLine, RiMore2Fill } from '@remixicon/react';

import { ButtonIcon, Menu, MenuItem, MenuTrigger, Text } from '@backstage/ui';

import { useCollaborativeDocument } from '@spotify/confidence-editor-react';
import { AttachmentFragment } from '@spotify/confidence-graphql';
import { MarkdownContent } from '@spotify/plugin-confidence-chat-react';
import {
  ArtifactIcon,
  ArtifactListItem,
  createArtifactExtension,
  getResourceName,
  isExternalLink,
} from '@spotify/plugin-confidence-projects-react';

import { projectsPlugin } from '../plugin';

const useStyles = makeStyles({
  primaryText: {
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  },
  secondaryText: {
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  },
  loading: {
    alignItems: 'center',
    display: 'flex',
    justifyContent: 'center',
    minHeight: 120,
  },
});

const reflowSingleLineTables = (markdown: string): string =>
  markdown.replace(/\|(?:[^|\n]*\|)+/g, table => {
    if (table.includes('\n')) return table;
    const separator = table.match(/\|(?::?-+:?\|)+/);
    if (!separator) return table;
    const cols = (separator[0].match(/:?-+:?/g) ?? []).length;
    if (cols === 0) return table;

    const pipesPerRow = cols + 1;
    const rows: string[] = [];
    let rowStart = 0;
    let rowPipes = 0;
    for (let i = 0; i < table.length; i++) {
      if (table[i] !== '|') continue;
      rowPipes++;
      if (rowPipes === pipesPerRow) {
        rows.push(table.slice(rowStart, i + 1).trim());
        rowStart = i + 1;
        rowPipes = 0;
      }
    }
    if (rows.length < 2) return table;
    return `\n\n${rows.join('\n')}\n\n`;
  });

const DocumentArtifactContent = ({
  documentName,
}: {
  documentName: string;
}) => {
  const classes = useStyles();
  const { collaborativeDocument, isLoading } = useCollaborativeDocument({
    name: documentName,
  });

  if (isLoading && !collaborativeDocument) {
    return (
      <div className={classes.loading}>
        <CircularProgress size={24} />
      </div>
    );
  }

  const markdown = collaborativeDocument?.markdown;
  if (!markdown) {
    return (
      <Text variant="body-small" color="secondary">
        This document is empty.
      </Text>
    );
  }

  return <MarkdownContent>{reflowSingleLineTables(markdown)}</MarkdownContent>;
};

const isDocumentArtifact = (artifact: AttachmentFragment) =>
  !isExternalLink(artifact.uri) &&
  getResourceName(artifact.uri).startsWith('collaborativeDocuments/');

const DocumentArtifactListItem = ({
  artifact,
  onSelect,
  onDelete,
}: {
  artifact: AttachmentFragment;
  onSelect: () => void;
  onDelete?: () => void;
}) => {
  const classes = useStyles();
  const dateLabel = new Date(artifact.createTime).toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
  });

  return (
    <ArtifactListItem.Root onClick={onSelect}>
      <ArtifactListItem.Icon>
        <ArtifactIcon>
          <RiFileLine size={16} />
        </ArtifactIcon>
      </ArtifactListItem.Icon>
      <ArtifactListItem.Content
        primary={
          <Text variant="body-medium" as="div" className={classes.primaryText}>
            {artifact.displayName || 'Untitled document'}
          </Text>
        }
        secondary={
          <Text
            variant="body-x-small"
            as="div"
            color="secondary"
            className={classes.secondaryText}
          >
            Document &middot; {dateLabel}
          </Text>
        }
      />
      <ArtifactListItem.Action>
        <MenuTrigger>
          <ButtonIcon
            variant="tertiary"
            size="small"
            icon={<RiMore2Fill size={16} />}
            aria-label="Document actions"
          />
          <Menu placement="bottom end">
            <MenuItem iconStart={<RiFileLine />} onClick={onSelect}>
              Open document
            </MenuItem>
            {onDelete && (
              <MenuItem
                color="danger"
                iconStart={<RiDeleteBinLine />}
                onClick={onDelete}
              >
                Delete
              </MenuItem>
            )}
          </Menu>
        </MenuTrigger>
      </ArtifactListItem.Action>
    </ArtifactListItem.Root>
  );
};

export const DocumentArtifactExtension = projectsPlugin.provide(
  createArtifactExtension({
    id: 'document',
    predicate: isDocumentArtifact,
    icon: () => ({ Icon: RiFileLine, color: '#6366F1' }),
    label: 'Document',
    category: 'Documents',
    render: artifact => (
      <DocumentArtifactContent documentName={getResourceName(artifact.uri)} />
    ),
    renderListItem: (artifact, { onSelect, onDelete }) => (
      <DocumentArtifactListItem
        artifact={artifact}
        onSelect={onSelect}
        onDelete={onDelete}
      />
    ),
  }),
);
